// The look the forge lays on a board (docs/186 EA13; docs/166 E3, E4): a room's own floor on each square it names,
// a runner along its line, and a mine's rails. The forge decides where; this only draws them. A floor name the set
// lacks, or a square that isn't open floor, is drawn as it would be with no look.
import type { Piece } from '../types';
import { Canvas, type Turn } from './canvas';
import { C, type Ctx } from './context';
import { groundOf, type Ground } from './ground';

type Dir = 'n' | 'e' | 's' | 'w';
const DIRS: ReadonlyArray<readonly [Dir, number, number]> = [['n', 0, -1], ['e', 1, 0], ['s', 0, 1], ['w', -1, 0]];

/** The piece a look names on each square, or undefined: only open floor squares, only names the set has. */
export function lookFloors(ctx: Ctx): Array<string | undefined> {
  const { sq } = ctx, { w, h } = sq, look = sq.board.look;
  const out: Array<string | undefined> = new Array(w * h).fill(undefined);
  if (!look?.floors) return out;
  for (let i = 0; i < w * h; i++) {
    const v = look.floors[i]!;
    if (!v || sq.sq[i] !== 'floor') continue;
    const name = look.names[v - 1];
    if (name && (name === 'runner' || name.startsWith('floor_')) && ctx.has(name)) out[i] = name;
  }
  return out;
}

/** What a hook chose that is only the kit's everyday ground, so a look floor takes its place (not piers, banks, ice). */
const GENERIC = new Set(['floor', 'floor_patch', 'grass']);

/**
 * The ground of a square with a look floor (`g` is what the painter and the hooks chose). A runner lies over the
 * floor its sides have: a room's floor beside it, else the kit's.
 */
export function lookGround(ctx: Ctx, floors: ReadonlyArray<string | undefined>, x: number, y: number, g: Ground | undefined): Ground | undefined {
  const { w } = ctx.sq;
  let name = floors[y * w + x];
  if (!name || (g && !GENERIC.has(g.name))) return g;
  if (name === 'runner') {
    // its sides first, then along it, then the corners: a runner lies in a room, on that room's floor
    const across = runsAcross(ctx, floors, x, y);
    const near: Array<[number, number]> = across ? [[0, -1], [0, 1], [-1, 0], [1, 0]] : [[-1, 0], [1, 0], [0, -1], [0, 1]];
    near.push([-1, -1], [1, -1], [-1, 1], [1, 1]);
    name = near.map(([dx, dy]) => (ctx.sq.on(x + dx, y + dy) && ctx.sq.at(x + dx, y + dy) === 'floor' ? floors[(y + dy) * w + x + dx] ?? '' : undefined)).find((n) => n !== undefined && n !== 'runner');
    if (!name) return g;
  }
  return groundOf(ctx, name, name, x, y) ?? g;
}

/**
 * A lone pillar in a room (EA6) stands on that room's floor: the look's name on the first open square beside it, or
 * undefined (the kit's floor).
 */
export function pillarFloor(ctx: Ctx, floors: ReadonlyArray<string | undefined>, x: number, y: number): string | undefined {
  for (const [, dx, dy] of DIRS) {
    if (ctx.sq.at(x + dx, y + dy) !== 'floor') continue;
    const n = floors[(y + dy) * ctx.sq.w + x + dx];
    return n === 'runner' ? undefined : n;
  }
  return undefined;
}

// ---- the runner ----

const runAt = (ctx: Ctx, floors: ReadonlyArray<string | undefined>, x: number, y: number): boolean => ctx.sq.on(x, y) && floors[y * ctx.sq.w + x] === 'runner';
/** A runner square runs up and down if a runner is above or below it, else across if one is beside it. */
function runsAcross(ctx: Ctx, floors: ReadonlyArray<string | undefined>, x: number, y: number): boolean {
  const run = (a: number, b: number): boolean => runAt(ctx, floors, a, b);
  return !run(x, y - 1) && !run(x, y + 1) && (run(x - 1, y) || run(x + 1, y));
}

/**
 * Rows at each end of a runner's file that are its hem (transparent, or one plain colour; `clearOnly`: transparent
 * only, for rails): filled from its body where the run goes on.
 */
function hemsOf(p: Piece, clearOnly = false): [number, number] {
  const cover = (r: number): number => { let n = 0; for (let u = 0; u < p.w; u++) if (p.px[(r * p.w + u) * 4 + 3] === 255) n++; return n; };
  const plain = (r: number): boolean => {
    let first = -1;
    for (let u = 0; u < p.w; u++) {
      const i = (r * p.w + u) * 4;
      if (p.px[i + 3] !== 255) continue;
      const c = (p.px[i]! << 16) | (p.px[i + 1]! << 8) | p.px[i + 2]!;
      if (first < 0) first = c; else if (c !== first) return false;
    }
    return true;
  };
  let full = 0;
  for (let r = 0; r < p.h; r++) full = Math.max(full, cover(r));
  const hem = (r: number): boolean => (clearOnly ? cover(r) === 0 : cover(r) < full || plain(r));
  let top = 0, bottom = 0;
  while (top < 2 && hem(top)) top++;
  while (bottom < 2 && hem(p.h - 1 - bottom)) bottom++;
  return p.h - top - bottom >= p.h / 2 ? [top, bottom] : [0, 0];
}

const runners = new WeakMap<Piece, Piece[]>();
/**
 * The file with its top hem (bit 1) and/or bottom hem (bit 2) filled from its body, so the next square's runs
 * straight on (bit 4: transparent rows only are hem, as a rail's).
 */
function runnerOf(p: Piece, joins: number): Piece {
  let made = runners.get(p);
  if (!made) runners.set(p, (made = []));
  const have = made[joins];
  if (have) return have;
  const [top, bottom] = hemsOf(p, (joins & 4) !== 0);
  const px = new Uint8ClampedArray(p.px);
  const row = (to: number, from: number) => px.set(p.px.subarray(from * p.w * 4, (from + 1) * p.w * 4), to * p.w * 4);
  const end = p.h - 1 - bottom;
  if (joins & 1) for (let k = 0; k < top; k++) row(k, end - (top - 1 - k));
  if (joins & 2) for (let k = 0; k < bottom; k++) row(end + 1 + k, top + k);
  return (made[joins] = { ...p, px });
}

/** The columns a runner's file covers (its strip across), first and last. */
function spanOf(p: Piece): [number, number] {
  let a = p.w, b = -1;
  for (let v = 0; v < p.h; v++) for (let u = 0; u < p.w; u++) if (p.px[(v * p.w + u) * 4 + 3]) { a = Math.min(a, u); b = Math.max(b, u); }
  return b < 0 ? [0, p.w - 1] : [a, b];
}

/**
 * Runners (docs/164: a carpet strip, its art up and down): a square runs up and down if a runner is above or below
 * it, across (turned a quarter: a runner is cloth with no light from one side, so it may turn, unless its kit's
 * `_upright` lists it) if one is beside it. Where the next square along is a runner too, the hem at that end is
 * filled from the body, so a run reads as one carpet and only its ends show their hems. Where runs meet (a corner,
 * a T, a crossing) each way out is drawn from the middle, the strip across laid first and the strip up and down over it.
 */
export function paintRunners(ctx: Ctx, out: Canvas, floors: ReadonlyArray<string | undefined>): void {
  const { w, h } = ctx.sq;
  const run = (x: number, y: number): boolean => runAt(ctx, floors, x, y);
  const upright = ((ctx.set.prompts['_upright'] as string[] | undefined) ?? []).includes('runner');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!run(x, y)) continue;
    const p = ctx.pick('runner', 'runner', x, y);
    if (!p) continue;
    const n = run(x, y - 1), s = run(x, y + 1), e = !upright && run(x + 1, y), wst = !upright && run(x - 1, y);
    const X = x * C, Y = y * C;
    // the file's top end lies north up and down, east once turned a quarter clockwise
    const across = (): Piece => runnerOf(p, (e ? 1 : 0) | (wst ? 2 : 0));
    const down = (): Piece => runnerOf(p, (n ? 1 : 0) | (s ? 2 : 0));
    if (!(e || wst)) { out.draw(down(), X, Y); continue; }
    if (!(n || s)) { out.draw(across(), X, Y, { rot90: true }); continue; }
    // runs meet here: each strip from the middle to the side it leaves by, reaching past the middle to the far
    // side of the other strip so the turn is filled
    const [c0, c1] = spanOf(p);
    drawClipped(out, across(), X, Y, { rot90: true }, wst ? 0 : c0, e ? C : c1 + 1, 0, C);
    drawClipped(out, down(), X, Y, undefined, 0, C, n ? 0 : c0, s ? C : c1 + 1);
  }
}

/** A piece laid with its top left at (X, Y), only its pixels in columns u0…u1-1 and rows v0…v1-1 of the square. */
function drawClipped(out: Canvas, p: Piece, X: number, Y: number, turn: Turn | undefined, u0: number, u1: number, v0: number, v1: number): void {
  for (let v = v0; v < v1; v++) for (let u = u0; u < u1; u++) {
    const c = Canvas.sample(p, u, v, turn);
    if (c[3]) out.over(X + u, Y + v, c);
  }
}

// ---- rails ----

/** Where each rail file's line leaves its square, as drawn (docs/166 C2 "Rails (E4)"). */
const RAILS: ReadonlyArray<readonly [string, readonly Dir[]]> = [
  ['rails', ['n', 's']], ['rails_across', ['e', 'w']], ['rails_bend', ['s', 'e']], ['rails_fork', ['n', 's', 'e']], ['rails_end', ['s']],
];
const TURNS: ReadonlyArray<Turn | undefined> = [undefined, { flipX: true }, { flipY: true }, { flipX: true, flipY: true }, { rot90: true }, { rot90: true, flipX: true }, { rot90: true, flipY: true }, { rot90: true, flipX: true, flipY: true }];
const CW: Record<Dir, Dir> = { n: 'e', e: 's', s: 'w', w: 'n' };

/** Where a side of a file ends up once laid with `turn` (canvas.ts: flipped, then turned a quarter clockwise). */
export function turned(d: Dir, turn: Turn | undefined): Dir {
  let o = d;
  if (turn?.flipX && (o === 'e' || o === 'w')) o = o === 'e' ? 'w' : 'e';
  if (turn?.flipY && (o === 'n' || o === 's')) o = o === 'n' ? 's' : 'n';
  return turn?.rot90 ? CW[o] : o;
}

const key = (ds: Iterable<Dir>): string => [...ds].sort().join('');

/**
 * The rail files (and how each is laid) for a square whose line leaves by `sides`: a straight, a bend, a fork or an
 * end, turned or flipped from the files the kit has. A crossing is the two straights over each other. A shape the
 * kit has no file for (the temperate mine has only the straight) is drawn as half straights from the middle out.
 */
export function railPieces(have: (piece: string) => boolean, sides: readonly Dir[]): Array<{ piece: string; turn?: Turn; half?: Dir }> {
  const want = key(sides);
  const straight = (d: Dir): { piece: string; turn?: Turn } => (d === 'n' || d === 's' || !have('rails_across') ? { piece: 'rails', ...(d === 'e' || d === 'w' ? { turn: { rot90: true } } : {}) } : { piece: 'rails_across' });
  if (sides.length === 0) return [{ piece: 'rails' }];
  if (sides.length === 4) return [straight('n'), straight('e')];
  // a file as drawn before any turned one (the kit's own `rails_across` before a turned straight)
  for (const turn of TURNS) for (const [piece, ends] of RAILS) {
    if (have(piece) && key(ends.map((d) => turned(d, turn))) === want) return [{ piece, ...(turn ? { turn } : {}) }];
  }
  // a straight run with no file for its shape: the straight, or a half for each way out
  if (want === 'ns' || want === 'ew') return [straight(sides[0]!)];
  return sides.map((d) => ({ ...straight(d), half: d }));
}

/** The forge's rails (docs/166 E4), over the floor and under everything standing; each square's file from its rail neighbours. */
export function paintRails(ctx: Ctx, out: Canvas): void {
  const { sq } = ctx, { w, h } = sq, rails = sq.board.look?.rails;
  if (!rails || !ctx.has('rails')) return;
  const on = (x: number, y: number): boolean => { const s = sq.at(x, y); return !!s && rails[y * w + x] === 1 && s !== 'wall' && s !== 'rock' && s !== 'void' && s !== 'water' && s !== 'deep' && s !== 'chasm'; };
  const have = (p: string): boolean => ctx.has(p);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (!on(x, y)) continue;
    const sides = DIRS.filter(([, dx, dy]) => on(x + dx, y + dy)).map(([d]) => d);
    for (const r of railPieces(have, sides)) {
      let p = ctx.pick(r.piece, 'rail', x, y);
      if (!p) continue;
      // a straight's clear end rows run on into the next square's (the temperate mine's rails stop short of the edge)
      if (r.piece === 'rails') {
        const [top, bottom]: Dir[] = r.turn?.rot90 ? ['e', 'w'] : ['n', 's'];
        p = runnerOf(p, 4 | (sides.includes(top!) ? 1 : 0) | (sides.includes(bottom!) ? 2 : 0));
      }
      if (!r.half) { out.draw(p, x * C, y * C, r.turn); continue; }
      // half a straight: from the middle to the side the line leaves by
      const [u0, u1, v0, v1] = r.half === 'n' ? [0, C, 0, C / 2] : r.half === 's' ? [0, C, C / 2, C] : r.half === 'w' ? [0, C / 2, 0, C] : [C / 2, C, 0, C];
      for (let v = v0; v < v1; v++) for (let u = u0; u < u1; u++) {
        const src = Canvas.sample(p, u, v, r.turn);
        if (src[3]) out.over(x * C + u, y * C + v, src);
      }
    }
  }
}
